import * as React from 'react';
import { CalendarDays } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../button';
import { Input } from '../input';
import { FormField } from '../form-field';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

/**
 * Time-period selector — the canonical "today / yesterday / 3d / 7d / 15d / 30d / custom"
 * control used across every demo page (per `01-build-brief.md` §5).
 *
 * Resolves a period to ISO `from`/`to` timestamps in Asia/Dubai. The DB
 * clock is UTC; queries CONVERT_TZ on their end. The selector emits
 * UTC-equivalent ISO strings so the proxy registry doesn't need to think
 * about timezones.
 *
 * URL-syncs `period` and `from`/`to` so reload preserves the choice.
 */

const PRESET_KEYS = [
  'today',
  'yesterday',
  'day_before_yesterday',
  '3d',
  '7d',
  '15d',
  '30d',
  'custom',
] as const;
export type TimePeriodKey = (typeof PRESET_KEYS)[number];

export interface TimePeriodValue {
  /** Preset key. 'custom' implies the from/to were user-picked. */
  period: TimePeriodKey;
  /** ISO timestamp of period start (inclusive). */
  from: string;
  /** ISO timestamp of period end (exclusive). */
  to: string;
  /** ISO timestamp of equal-length prior period start. */
  prevFrom: string;
  /** ISO timestamp of equal-length prior period end. */
  prevTo: string;
}

const LABELS: Record<TimePeriodKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  day_before_yesterday: '2 days ago',
  '3d': '3d',
  '7d': '7d',
  '15d': '15d',
  '30d': '30d',
  custom: 'Custom',
};

/** Asia/Dubai is UTC+4. Compute "today" with that anchor. */
const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;

function dubaiNow(): Date {
  return new Date(Date.now() + DUBAI_OFFSET_MS);
}

function startOfDubaiDay(d: Date): Date {
  // d is the Dubai-shifted "wall clock"; floor it to start-of-day.
  const shifted = new Date(d.getTime());
  shifted.setUTCHours(0, 0, 0, 0);
  // Convert back to UTC.
  return new Date(shifted.getTime() - DUBAI_OFFSET_MS);
}

function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86_400_000);
}

/** `YYYY-MM-DD` (as picked from a native date input) -> Dubai-midnight UTC instant. */
function dubaiDayStartFromIso(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!) - DUBAI_OFFSET_MS);
}

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

// A hand-edited or truncated shared link (`?period=custom&from=2026-08&to=x`)
// must degrade to a default period, not crash — dubaiDayStartFromIso has no
// input validation, so 'x'.split('-').map(Number) -> [NaN] -> an Invalid
// Date whose later .toISOString() throws RangeError.
function parseDubaiDay(day: string | null): Date | null {
  if (!day || !ISO_DAY_RE.test(day)) return null;
  const d = dubaiDayStartFromIso(day);
  if (Number.isNaN(d.getTime())) return null;
  // Date.UTC silently normalizes overflowing days (2026-02-31 -> March 3),
  // so a shape-valid but calendar-invalid input would otherwise pass
  // through as a different date. Round-trip and reject any mismatch.
  return isoDayInDubai(d) === day ? d : null;
}

function isoDayInDubai(d: Date): string {
  const shifted = new Date(d.getTime() + DUBAI_OFFSET_MS);
  return shifted.toISOString().slice(0, 10);
}

export function resolvePeriod(period: TimePeriodKey, custom?: { from: Date; to: Date }): TimePeriodValue {
  const dubai = dubaiNow();
  const todayStart = startOfDubaiDay(dubai);
  const tomorrowStart = addDays(todayStart, 1);

  let from: Date;
  let to: Date;

  switch (period) {
    case 'today':
      from = todayStart;
      to = tomorrowStart;
      break;
    case 'yesterday':
      from = addDays(todayStart, -1);
      to = todayStart;
      break;
    case 'day_before_yesterday':
      from = addDays(todayStart, -2);
      to = addDays(todayStart, -1);
      break;
    case '3d':
      from = addDays(todayStart, -2);
      to = tomorrowStart;
      break;
    case '7d':
      from = addDays(todayStart, -6);
      to = tomorrowStart;
      break;
    case '15d':
      from = addDays(todayStart, -14);
      to = tomorrowStart;
      break;
    case '30d':
      from = addDays(todayStart, -29);
      to = tomorrowStart;
      break;
    case 'custom':
      if (!custom) {
        from = addDays(todayStart, -6);
        to = tomorrowStart;
      } else {
        from = custom.from;
        to = custom.to;
      }
      break;
  }

  const lengthMs = to.getTime() - from.getTime();
  const prevTo = from;
  const prevFrom = new Date(from.getTime() - lengthMs);

  return {
    period,
    from: from.toISOString(),
    to: to.toISOString(),
    prevFrom: prevFrom.toISOString(),
    prevTo: prevTo.toISOString(),
  };
}

export interface TimePeriodSelectorProps {
  value: TimePeriodValue;
  onChange: (next: TimePeriodValue) => void;
  /** Show a Refresh button alongside the presets. */
  onRefresh?: () => void;
  /**
   * Reflect the caller's in-flight refresh on the optional Refresh button.
   * Kept opt-in because most consumers do not expose a manual refresh.
   */
  isRefreshing?: boolean;
  className?: string;
  /**
   * Caps the custom-range picker's span in days. Optional and undefined
   * by default (no cap) — this component is shared across 6+ report
   * tabs, most with no such constraint; only a consumer whose backing
   * query has its own hard limit (e.g. the Sales Dashboard's daily-trend
   * RPC, capped at 366 days server-side) should pass this, scoped to
   * that one usage rather than a global change (B63 Phase 4b, Codex
   * brief-gate review).
   */
  maxCustomRangeDays?: number;
}

export function TimePeriodSelector({
  value,
  onChange,
  onRefresh,
  isRefreshing = false,
  className,
  maxCustomRangeDays,
}: TimePeriodSelectorProps) {
  // isoDayInDubai applies the +4h shift itself — pass the real instant,
  // not dubaiNow()'s already-shifted one, or the last 4 Dubai hours of
  // the day double-shift into tomorrow.
  const today = isoDayInDubai(new Date());
  const [open, setOpen] = React.useState(false);
  // Custom's `to` is exclusive (matches every other preset's contract), so
  // the draft "to" day shown to the user is one day earlier than value.to.
  const [draftFrom, setDraftFrom] = React.useState(() => isoDayInDubai(new Date(value.from)));
  const [draftTo, setDraftTo] = React.useState(() =>
    isoDayInDubai(addDays(new Date(value.to), -1)),
  );

  const [orderedFrom, orderedTo] =
    draftFrom && draftTo && draftFrom > draftTo ? [draftTo, draftFrom] : [draftFrom, draftTo];
  const beyondToday = Boolean(orderedTo) && orderedTo > today;
  const rangeSpanDays =
    orderedFrom && orderedTo
      ? Math.round(
          (dubaiDayStartFromIso(orderedTo).getTime() - dubaiDayStartFromIso(orderedFrom).getTime()) /
            86_400_000,
        ) + 1
      : 0;
  const exceedsMaxRange = Boolean(maxCustomRangeDays) && rangeSpanDays > maxCustomRangeDays!;
  const canApply = Boolean(orderedFrom && orderedTo) && !beyondToday && !exceedsMaxRange;

  const applyCustom = () => {
    if (!canApply) return;
    onChange(
      resolvePeriod('custom', {
        from: dubaiDayStartFromIso(orderedFrom),
        // Custom's `to` is exclusive — the picked end day must itself be
        // included, so the instant is the START of the day AFTER it.
        to: addDays(dubaiDayStartFromIso(orderedTo), 1),
      }),
    );
    setOpen(false);
  };

  const customLabel =
    value.period === 'custom'
      ? `${isoDayInDubai(new Date(value.from)).slice(5)} → ${isoDayInDubai(addDays(new Date(value.to), -1)).slice(5)}`
      : 'Custom';

  return (
    <div className={cn('inline-flex flex-wrap items-center gap-2', className)}>
      <div
        role="group"
        aria-label="Report period"
        className="inline-flex flex-wrap items-center rounded-md border border-border bg-bg-surface p-1 shadow-sm"
      >
        {PRESET_KEYS.filter((k) => k !== 'custom').map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={value.period === preset}
            onClick={() => onChange(resolvePeriod(preset))}
            className={cn(
              'inline-flex min-h-11 min-w-11 items-center justify-center rounded px-3 text-caption font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
              value.period === preset
                ? 'bg-accent-500 text-text-on-brand shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
            )}
          >
            {LABELS[preset]}
          </button>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-pressed={value.period === 'custom'}
              onClick={() => {
                setDraftFrom(isoDayInDubai(new Date(value.from)));
                setDraftTo(isoDayInDubai(addDays(new Date(value.to), -1)));
              }}
              className={cn(
                'inline-flex min-h-11 min-w-11 items-center justify-center gap-1 rounded px-3 text-caption font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
                value.period === 'custom'
                  ? 'bg-accent-500 text-text-on-brand shadow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
              )}
            >
              <CalendarDays className="h-3 w-3" aria-hidden />
              {customLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-4">
            <div className="mb-3 text-caption font-medium text-text-primary">Custom range</div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="From">
                <Input
                  type="date"
                  value={draftFrom}
                  max={draftTo || today}
                  onChange={(e) => setDraftFrom(e.target.value)}
                />
              </FormField>
              <FormField label="To">
                <Input
                  type="date"
                  value={draftTo}
                  min={draftFrom || undefined}
                  max={today}
                  onChange={(e) => setDraftTo(e.target.value)}
                />
              </FormField>
            </div>
            {beyondToday ? (
              <p role="alert" className="mt-3 text-caption" style={{ color: 'var(--danger-text)' }}>
                The end date can't be in the future.
              </p>
            ) : exceedsMaxRange ? (
              <p role="alert" className="mt-3 text-caption" style={{ color: 'var(--danger-text)' }}>
                Range can't exceed {maxCustomRangeDays} days.
              </p>
            ) : null}
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={applyCustom} disabled={!canApply}>
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {onRefresh ? (
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md border border-border bg-bg-surface px-3 text-caption font-medium text-text-secondary',
            'shadow-sm transition-colors hover:text-text-primary hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
          )}
          aria-busy={isRefreshing || undefined}
          aria-label={isRefreshing ? 'Refreshing' : 'Refresh'}
          title={isRefreshing ? 'Refreshing' : 'Refresh'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
            <path d="M21 3v5h-5" />
          </svg>
          {isRefreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      ) : null}
    </div>
  );
}

/** Read/write the period in URLSearchParams. */
export function periodFromSearchParams(params: URLSearchParams): TimePeriodValue {
  const raw = params.get('period') ?? '7d';
  if (raw === 'custom') {
    // 'custom' alone can't be resolved — it needs the picked from/to back.
    // Round-trip through dubaiDayStartFromIso so a reload/shared-link
    // restores the exact picked days instead of silently falling back to
    // resolvePeriod('custom')'s undated 7d-equivalent window.
    const fromDay = parseDubaiDay(params.get('from'));
    const toDay = parseDubaiDay(params.get('to'));
    // Both individually valid doesn't mean the pair is valid to resolve:
    // - reversed (from later than to) -> negative-length window, every
    //   downstream Chat Ops consumer would see p_start > p_end.
    // - either boundary beyond today -> a range the picker UI itself
    //   can't produce (it caps at today), so it must have come from a
    //   hand-edited/shared URL; consumers would forward a future p_end
    //   and get empty buckets instead of a real report.
    const todayDay = parseDubaiDay(isoDayInDubai(new Date()));
    const isValidRange =
      fromDay &&
      toDay &&
      fromDay.getTime() <= toDay.getTime() &&
      (!todayDay || (fromDay.getTime() <= todayDay.getTime() && toDay.getTime() <= todayDay.getTime()));
    if (isValidRange) {
      return resolvePeriod('custom', {
        from: fromDay,
        to: addDays(toDay, 1),
      });
    }
    return resolvePeriod('custom');
  }
  if (PRESET_KEYS.includes(raw as TimePeriodKey)) {
    return resolvePeriod(raw as TimePeriodKey);
  }
  return resolvePeriod('7d');
}

export function periodToSearchParams(period: TimePeriodValue, params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set('period', period.period);
  if (period.period === 'custom') {
    next.set('from', isoDayInDubai(new Date(period.from)));
    next.set('to', isoDayInDubai(addDays(new Date(period.to), -1)));
  } else {
    next.delete('from');
    next.delete('to');
  }
  return next;
}
